import type {
  ConversationMode,
} from './conversationPersistence';

import {
  getSessionAuthorizationHeaders,
} from './sessionAuth';

export type ReportCategory =
  | 'incorrect_response'
  | 'misunderstood_input'
  | 'technical_problem'
  | 'other';

interface SessionResponse {
  ok: boolean;
  authenticated?: boolean;
  csrf_token?: string;
  error?: string;
}

interface ReportResponse {
  ok: boolean;
  error?: string;
  report?: {
    id: number;
    conversation_included: boolean;
    snapshot_message_count: number;
  };
}

export interface SubmitProblemReportInput {
  language: string;
  mode: ConversationMode;
  category: ReportCategory;
  comment: string;
  includeConversation: boolean;
}

export interface SubmittedProblemReport {
  id: number;
  conversationIncluded: boolean;
  snapshotMessageCount: number;
}

export class ProblemReportRequestError extends Error {
  status: number;
  code?: string;

  constructor(
    message: string,
    status: number,
    code?: string
  ) {
    super(message);
    this.name = 'ProblemReportRequestError';
    this.status = status;
    this.code = code;
  }
}

let csrfTokenPromise: Promise<string> | null = null;

const getCsrfToken = async (): Promise<string> => {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch('/api/session.php', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...getSessionAuthorizationHeaders(),
        Accept: 'application/json',
      },
    })
      .then(async response => {
        const data =
          (await response.json()) as SessionResponse;

        if (
          !response.ok ||
          !data.ok ||
          !data.authenticated ||
          !data.csrf_token
        ) {
          throw new ProblemReportRequestError(
            'No fue posible validar tu sesión.',
            response.status,
            data.error
          );
        }

        return data.csrf_token;
      })
      .catch(error => {
        csrfTokenPromise = null;
        throw error;
      });
  }

  return csrfTokenPromise;
};

const getClientContext = () => ({
  viewport_width: window.innerWidth,
  viewport_height: window.innerHeight,
  screen_width: window.screen.width,
  screen_height: window.screen.height,
  device_pixel_ratio: window.devicePixelRatio,
  platform: navigator.platform,
  language: navigator.language,
  online: navigator.onLine,
  embedded: window.self !== window.top,
  page_path: window.location.pathname,
});

export const submitProblemReport = async (
  input: SubmitProblemReportInput
): Promise<SubmittedProblemReport> => {
  const csrfToken = await getCsrfToken();

  const response = await fetch('/api/report.php', {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      ...getSessionAuthorizationHeaders(),
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      language: input.language,
      mode: input.mode,
      category: input.category,
      comment: input.comment.trim(),
      include_conversation: input.includeConversation,
      app_version: 'report-ui-v1-20260729',
      client_context: getClientContext(),
    }),
  });

  const data =
    (await response.json()) as ReportResponse;

  if (!response.ok || !data.ok || !data.report) {
    if (response.status === 401 || response.status === 403) {
      csrfTokenPromise = null;
    }

    throw new ProblemReportRequestError(
      'No fue posible enviar el reporte.',
      response.status,
      data.error
    );
  }

  return {
    id: data.report.id,
    conversationIncluded:
      data.report.conversation_included,
    snapshotMessageCount:
      data.report.snapshot_message_count,
  };
};
